import { uOvercast, uRainAmt, uLightning } from '../core/uniforms.js';
import { streamRng, range, weighted } from '../math/rng.js';
import { clamp, damp, saturate } from '../math/mathx.js';

/**
 * @file THE WEATHER. A seeded schedule over five states, three damped uniform
 * ramps, and a storm's lightning scheduler — and NOT ONE LINE OF three.js, so
 * `tests/weather.test.mjs` can simulate a week of it in node.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE SHAPE OF IT
 * ─────────────────────────────────────────────────────────────────────────────
 * `DayNight` is the model: one object owns a value, writes it into global
 * uniforms, and pushes it at whatever visual systems are BOUND to it, duck-typed
 * so nothing has to import anything. Weather is the same object for cloud.
 *
 *   uOvercast   grade the whole world (`render/nodes.js#weatherGrade`)
 *   uRainAmt    streak density/length/alpha, ocean chop
 *   uLightning  the flash — world grade AND the sky dome
 *   sky.setWeather({overcast})            grey the stops, kill the halo/stars
 *   fog.setWeather({densityMul, grey})    thicken and decolour the haze
 *   rain.setAmount(rain)                  `instanceCount` + `visible`
 *
 * Everything a MATERIAL needs is a uniform; everything a HANDLE needs is
 * pushed only when it actually changes, because `sky.setWeather` re-lerps four
 * colour stops and there is no reason to do that 60 times a second while the
 * weather is sitting still.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT WILL BITE
 * ─────────────────────────────────────────────────────────────────────────────
 * • **The schedule and the lightning draw from DIFFERENT streams.** `'weather'`
 *   picks states and dwells; `'weather:bolt'` picks flashes. Share one and the
 *   number of times a storm flashed decides what the weather does next — so a
 *   tuning change to the flash rate silently reshuffles the whole day. Same
 *   discipline as `systems/accuracy.js`'s dedicated `'aim'` stream, and the
 *   suite pins it.
 *
 * • **A flash always draws exactly THREE numbers**, even when the double-strike
 *   roll fails and the second and third are unused. A conditional draw makes the
 *   stream's position depend on a branch, which is the fastest way to make
 *   "same seed, same storm" quietly untrue.
 *
 * • **There is no CLEAR -> RAIN and no CLEAR -> STORM.** The chain is
 *   CLEAR <-> OVERCAST <-> RAIN <-> STORM, one rung at a time, because the whole
 *   point of a 6-12 s ramp is that you SEE the sky change its mind. FOG is off
 *   to the side: it is only entered from CLEAR or OVERCAST, only left to CLEAR
 *   or OVERCAST, and it is weighted up at dawn and down the rest of the day
 *   (`hourFn`, normally `() => dayNight.hour`).
 *
 * • **`?weather=` PINS the state.** No schedule, no transitions, ramps still
 *   run (so the pinned state arrives rather than snapping). That is the browser
 *   verification path and it must stay honest — a pin that snapped would hide
 *   every transition bug there is.
 *
 * • **Lightning is gated on the CLOUD, not on the STATE.** `uLightning` may only
 *   spike once `uOvercast` has actually ramped past `LIGHTNING.gate`; otherwise
 *   walking into a storm flashes a blue sky, which reads as a rendering fault.
 *
 * @module engine/systems/Weather
 */

/** @typedef {import('../types.js').EventBus} EventBus */
/** @typedef {import('../types.js').Rng} Rng */

/**
 * The five states and what each one asks the world for.
 *
 * `overcast` is a RAMP, so the states are a progression rather than a switch:
 * an overcast afternoon is not as heavy as a rainstorm and a rainstorm is not
 * as heavy as a thunderstorm, and `weatherGrade`'s desaturate/darken/cool is
 * authored against `1` = the worst of it.
 *
 * `fogMul` multiplies the whole fog density stack (base x night x this). The
 * city's base is 0.0011/m, so FOG's 5.0 puts a 200 m sight line at 33% contrast
 * — the far side of the island genuinely gone — while STORM's 2.2 only greys it.
 */
export const WEATHER = Object.freeze({
  clear: Object.freeze({
    id: 'clear', name: 'Clear', glyph: '☀',
    overcast: 0, rain: 0, fogMul: 1, fogGrey: 0, lightning: false,
  }),
  overcast: Object.freeze({
    id: 'overcast', name: 'Overcast', glyph: '☁',
    overcast: 0.7, rain: 0, fogMul: 1.6, fogGrey: 0.5, lightning: false,
  }),
  rain: Object.freeze({
    id: 'rain', name: 'Rain', glyph: '🌧',
    overcast: 0.88, rain: 0.72, fogMul: 1.9, fogGrey: 0.7, lightning: false,
  }),
  storm: Object.freeze({
    id: 'storm', name: 'Thunderstorm', glyph: '⛈',
    overcast: 1, rain: 1, fogMul: 2.2, fogGrey: 0.85, lightning: true,
  }),
  fog: Object.freeze({
    id: 'fog', name: 'Fog', glyph: '🌫',
    overcast: 0.45, rain: 0, fogMul: 5, fogGrey: 0.6, lightning: false,
  }),
});

/** @type {ReadonlyArray<string>} */
export const WEATHER_IDS = Object.freeze(Object.keys(WEATHER));

/**
 * `from -> [[to, weight], ...]`. THE legality contract; see the header for why
 * the chain has no shortcuts. Self-transitions are a legal "stay put another
 * dwell", which is what stops a fixed 90-240 s clock from feeling like a
 * metronome.
 * @type {Readonly<Object<string, ReadonlyArray<[string, number]>>>}
 */
export const WEATHER_TRANSITIONS = Object.freeze({
  clear: Object.freeze([['overcast', 0.62], ['fog', 0.2], ['clear', 0.18]]),
  overcast: Object.freeze([['clear', 0.38], ['rain', 0.4], ['fog', 0.12], ['overcast', 0.1]]),
  rain: Object.freeze([['overcast', 0.45], ['storm', 0.35], ['rain', 0.2]]),
  storm: Object.freeze([['rain', 0.75], ['storm', 0.25]]),
  fog: Object.freeze([['clear', 0.55], ['overcast', 0.45]]),
});

/** Seconds a state holds before the next roll. */
export const WEATHER_DWELL = Object.freeze({ min: 90, max: 240 });

/**
 * Damping rates (per second) for the three ramps, chosen so 95% of a change
 * lands inside the brief's 6-12 s window: `damp` reaches 95% at `3 / lambda`,
 * i.e. 8.8 s, 10.0 s and 10.7 s here. Cloud leads, rain follows, fog is the
 * laggard — which is the order the eye expects, because a sky darkens before it
 * rains and the air is still thick after it stops.
 */
export const WEATHER_RAMP = Object.freeze({ overcast: 0.34, rain: 0.3, fog: 0.28 });

/** Dawn window (hours) where FOG is weighted UP, and the two multipliers. */
export const FOG_HOURS = Object.freeze({ from: 4.5, to: 8.5, boost: 3.5, penalty: 0.3 });

/**
 * The storm's flash scheduler.
 *
 * `decay` is per second and the amplitude is `exp(-decay * t)`: at 12 that is
 * 0.05 after 0.25 s, i.e. "spikes to 1 and is gone in a quarter second" with
 * a curve rather than a cutoff. `gate` is the cloud cover the flash needs
 * before it may fire at all — see the header.
 */
export const LIGHTNING = Object.freeze({
  minGap: 6,
  maxGap: 16,
  decay: 12,
  doubleChance: 0.3,
  doubleGapMin: 0.12,
  doubleGapMax: 0.3,
  doubleAmp: 0.75,
  gate: 0.6,
  /** Below this the level is snapped to 0 so a dead storm writes a clean zero. */
  epsilon: 1e-4,
});

/** Emit cadence for the `'weather'` bus channel, seconds. */
const EMIT_INTERVAL = 0.2;

export class Weather {
  /**
   * @param {Object} [o]
   * @param {string|number} [o.seed='weather'] Same seed => same week of sky.
   * @param {string|null} [o.state] PIN this state (the `?weather=` override).
   *   Null runs the schedule.
   * @param {EventBus|null} [o.bus] Emits `'weather'` at ~5 Hz and on every state
   *   change — see {@link Weather#snapshot}.
   * @param {(() => number)|null} [o.hourFn] Reads the time of day (0..24) for
   *   the dawn fog bias. Normally `() => engine.dayNight.hour`. Omit and FOG is
   *   simply unweighted.
   * @param {string} [o.start] Initial state. Defaults to CLEAR (which is also
   *   the uniforms' zero, so nothing flashes on the first frame).
   * @param {boolean} [o.instant=false] Start with the ramps already arrived.
   */
  constructor(o = {}) {
    const {
      seed = 'weather',
      state = null,
      bus = null,
      hourFn = null,
      start = 'clear',
      instant = false,
    } = o;

    /** @type {Rng} States + dwells ONLY. See the header. */
    this._rng = streamRng(seed, 'weather');
    /** @type {Rng} Flashes ONLY. */
    this._bolt = streamRng(seed, 'weather:bolt');

    /** @type {EventBus|null} */
    this.bus = bus;
    /** @type {((s: ReturnType<Weather['snapshot']>) => void)|null} */
    this.onChange = null;
    /** @type {(() => number)|null} */
    this._hourFn = hourFn;

    const pinned = state && WEATHER[state] ? state : null;
    /** @type {string|null} `?weather=` pin; null means the schedule runs. */
    this._pinned = pinned;
    /** @type {string} */
    this._state = pinned ?? (WEATHER[start] ? start : 'clear');
    /** @type {number} Seconds left in this state's dwell. */
    this._dwell = pinned ? Infinity : this._rollDwell();
    /** @type {number} The dwell it started with, for the HUD's `t`. */
    this._dwell0 = this._dwell;

    // Live ramp values. They start at CLEAR's zeros unless `instant`, which is
    // what makes "no Weather system" and "a Weather system on frame 0" identical.
    const t = WEATHER[this._state];
    this._overcast = instant ? t.overcast : 0;
    this._rain = instant ? t.rain : 0;
    this._fogMul = instant ? t.fogMul : 1;
    this._fogGrey = instant ? t.fogGrey : 0;
    this._lightning = 0;

    this._nextFlash = this._rollGap();
    /** @type {{t: number, amp: number}|null} Queued double strike. */
    this._pending = null;

    /** @type {{sky: any, fog: any, rain: any}} */
    this._bound = { sky: null, fog: null, rain: null };
    /** Last values actually pushed at the bound handles (they are not cheap). */
    this._pushed = { overcast: NaN, fogMul: NaN, fogGrey: NaN, rain: NaN };

    this._emitAccum = 0;
    this._disposed = false;

    this._apply();
    this._emit(true);
  }

  // ── state ──────────────────────────────────────────────────────────────────

  /** @returns {string} */
  get state() {
    return this._state;
  }

  /** @returns {boolean} True when `?weather=` pinned it. */
  get pinned() {
    return this._pinned != null;
  }

  /** @returns {number} 0..1 live cloud cover. Mirrors `uOvercast`. */
  get overcast() {
    return this._overcast;
  }

  /** @returns {number} 0..1 live rain. Mirrors `uRainAmt`. */
  get rain() {
    return this._rain;
  }

  /** @returns {number} 0..1 live flash. Mirrors `uLightning`. */
  get lightning() {
    return this._lightning;
  }

  /** @returns {number} Fog density multiplier currently applied. */
  get fogMul() {
    return this._fogMul;
  }

  // ── control ────────────────────────────────────────────────────────────────

  /**
   * Forces a state. Used by the `?weather=` pin, the HUD's buttons and the
   * console (`__engine.weather.setState('storm')`).
   *
   * Draws NO random numbers when `pin` is set — a pin that consumed the
   * schedule stream would make "same seed, same week" depend on what the player
   * clicked.
   *
   * @param {string} id
   * @param {{instant?: boolean, pin?: boolean}} [opts] `instant` snaps the ramps
   *   (debug only — every transition bug hides behind it).
   * @returns {this}
   */
  setState(id, opts = {}) {
    if (this._disposed || !WEATHER[id]) return this;
    this._state = id;
    if (opts.pin) this._pinned = id;
    this._dwell = this._pinned ? Infinity : this._rollDwell();
    this._dwell0 = this._dwell;
    if (opts.instant) {
      const t = WEATHER[id];
      this._overcast = t.overcast;
      this._rain = t.rain;
      this._fogMul = t.fogMul;
      this._fogGrey = t.fogGrey;
    }
    this._apply();
    this._emit(true);
    return this;
  }

  /** Releases a `?weather=` pin and hands control back to the schedule. */
  unpin() {
    if (this._pinned == null) return this;
    this._pinned = null;
    this._dwell = this._rollDwell();
    this._dwell0 = this._dwell;
    return this;
  }

  // ── binding ────────────────────────────────────────────────────────────────

  /**
   * MERGE semantics, exactly like `DayNight.bind` — keys you omit keep their
   * binding, an explicit `null` unbinds. Duck-typed:
   *   `sky.setWeather({overcast})`
   *   `fog.setWeather({densityMul, grey})`
   *   `rain.setAmount(amount)`
   *
   * @param {{sky?: any, fog?: any, rain?: any}} targets
   * @returns {this}
   */
  bind(targets = {}) {
    if ('sky' in targets) this._bound.sky = targets.sky ?? null;
    if ('fog' in targets) this._bound.fog = targets.fog ?? null;
    if ('rain' in targets) this._bound.rain = targets.rain ?? null;
    // A fresh binding must not wait for the next ramp step to learn the weather.
    this._pushed = { overcast: NaN, fogMul: NaN, fogGrey: NaN, rain: NaN };
    this._apply();
    return this;
  }

  /**
   * @param {'sky'|'fog'|'rain'} [what] Omit to unbind everything.
   * @returns {this}
   */
  unbind(what) {
    if (what) this._bound[what] = null;
    else this._bound = { sky: null, fog: null, rain: null };
    return this;
  }

  // ── tick ───────────────────────────────────────────────────────────────────

  /**
   * Runs the schedule, the ramps and the flash. Called on the fixed step from
   * an `engine.systems` entry.
   * @param {number} dt Seconds.
   */
  update(dt) {
    if (this._disposed || !(dt > 0)) return;

    // 1. schedule
    if (this._pinned == null) {
      this._dwell -= dt;
      if (this._dwell <= 0) {
        this._state = this._pick(this._state);
        this._dwell = this._rollDwell();
        this._dwell0 = this._dwell;
        this._emit(true);
      }
    }

    // 2. ramps
    const t = WEATHER[this._state];
    this._overcast = damp(this._overcast, t.overcast, WEATHER_RAMP.overcast, dt);
    this._rain = damp(this._rain, t.rain, WEATHER_RAMP.rain, dt);
    this._fogMul = damp(this._fogMul, t.fogMul, WEATHER_RAMP.fog, dt);
    this._fogGrey = damp(this._fogGrey, t.fogGrey, WEATHER_RAMP.fog, dt);

    // 3. lightning
    this._stepLightning(dt, t.lightning);

    this._apply();

    this._emitAccum += dt;
    if (this._emitAccum >= EMIT_INTERVAL) this._emit(false);
  }

  /**
   * @param {number} dt
   * @param {boolean} armed True while the STATE wants lightning.
   */
  _stepLightning(dt, armed) {
    // Decay first, always — so leaving a storm mid-flash still fades out.
    if (this._lightning > 0) {
      this._lightning *= Math.exp(-LIGHTNING.decay * dt);
      if (this._lightning < LIGHTNING.epsilon) this._lightning = 0;
    }

    // A queued second strike survives disarming: half a double is worse than
    // none, and it lands inside 0.3 s anyway.
    if (this._pending) {
      this._pending.t -= dt;
      if (this._pending.t <= 0) {
        this._lightning = Math.max(this._lightning, this._pending.amp);
        this._pending = null;
      }
    }

    if (!armed || this._overcast < LIGHTNING.gate) return;

    this._nextFlash -= dt;
    if (this._nextFlash > 0) return;

    // THREE draws, unconditionally — see the header.
    const wantsDouble = this._bolt() < LIGHTNING.doubleChance;
    const gap = range(this._bolt, LIGHTNING.doubleGapMin, LIGHTNING.doubleGapMax);
    this._nextFlash = range(this._bolt, LIGHTNING.minGap, LIGHTNING.maxGap);

    this._lightning = 1;
    this._pending = wantsDouble ? { t: gap, amp: LIGHTNING.doubleAmp } : null;
  }

  /** Writes the uniforms and pushes anything that actually moved. */
  _apply() {
    uOvercast.value = saturate(this._overcast);
    uRainAmt.value = saturate(this._rain);
    uLightning.value = saturate(this._lightning);

    const p = this._pushed;
    if (this._bound.sky && !near(p.overcast, this._overcast)) {
      p.overcast = this._overcast;
      this._bound.sky.setWeather?.({ overcast: this._overcast });
    }
    if (this._bound.fog && (!near(p.fogMul, this._fogMul) || !near(p.fogGrey, this._fogGrey))) {
      p.fogMul = this._fogMul;
      p.fogGrey = this._fogGrey;
      this._bound.fog.setWeather?.({ densityMul: this._fogMul, grey: this._fogGrey });
    }
    if (this._bound.rain && !near(p.rain, this._rain)) {
      p.rain = this._rain;
      this._bound.rain.setAmount?.(this._rain);
    }
  }

  // ── schedule internals ─────────────────────────────────────────────────────

  /** @returns {number} */
  _rollDwell() {
    return range(this._rng, WEATHER_DWELL.min, WEATHER_DWELL.max);
  }

  /** @returns {number} */
  _rollGap() {
    return range(this._bolt, LIGHTNING.minGap, LIGHTNING.maxGap);
  }

  /**
   * ONE draw per transition, from the schedule stream.
   * @param {string} from
   * @returns {string}
   */
  _pick(from) {
    const rows = WEATHER_TRANSITIONS[from] ?? WEATHER_TRANSITIONS.clear;
    /** @type {Array<[string, number]>} */
    const pairs = [];
    for (const [to, w] of rows) pairs.push([to, to === 'fog' ? w * this._fogBias() : w]);
    return weighted(this._rng, pairs);
  }

  /**
   * Fog is a DAWN phenomenon. With no clock bound it is unweighted, which keeps
   * the system honest in a dev scene that has no `DayNight`.
   * @returns {number}
   */
  _fogBias() {
    if (!this._hourFn) return 1;
    const h = Number(this._hourFn());
    if (!Number.isFinite(h)) return 1;
    return h >= FOG_HOURS.from && h <= FOG_HOURS.to ? FOG_HOURS.boost : FOG_HOURS.penalty;
  }

  // ── reporting ──────────────────────────────────────────────────────────────

  /**
   * The HUD contract. `state` and `t` are the two fields the brief pins; the
   * rest is for the debug overlay and for anyone driving this from a console.
   *
   * `t` is DWELL progress, 0 at the moment the state was entered and 1 when the
   * next roll is due — not ramp progress, which the individual channels already
   * report.
   */
  snapshot() {
    const def = WEATHER[this._state];
    const t = this._pinned || !(this._dwell0 > 0) ? 0 : clamp(1 - this._dwell / this._dwell0, 0, 1);
    return {
      state: this._state,
      t,
      name: def.name,
      glyph: def.glyph,
      overcast: this._overcast,
      rain: this._rain,
      lightning: this._lightning,
      fogMul: this._fogMul,
      pinned: this._pinned != null,
      dwell: this._dwell === Infinity ? Infinity : Math.max(0, this._dwell),
    };
  }

  /** @param {boolean} force */
  _emit(force) {
    if (!force && this._emitAccum < EMIT_INTERVAL) return;
    this._emitAccum = 0;
    if (!this.bus && !this.onChange) return;
    const snap = this.snapshot();
    this.bus?.emit?.('weather', snap);
    this.onChange?.(snap);
  }

  /** Zeroes the three uniforms and drops every binding. Idempotent. */
  dispose() {
    if (this._disposed) return;
    this._disposed = true;
    this._bound.rain?.setAmount?.(0);
    this.unbind();
    this.onChange = null;
    this.bus = null;
    // Back to the documented zeros, or the next level built in this tab
    // inherits a storm it never asked for.
    uOvercast.value = 0;
    uRainAmt.value = 0;
    uLightning.value = 0;
  }
}

/** @param {number} a @param {number} b */
const near = (a, b) => Math.abs(a - b) < 1e-4;

/**
 * Reads the `?weather=` deep link. Mirrors `DayNight#hourFromQuery` exactly,
 * including the "no window, no answer" branch, so a headless import is safe.
 *
 * @param {string|URLSearchParams|null} [search] Defaults to `location.search`.
 * @returns {string|null} A {@link WEATHER} id, or null when absent/unknown.
 */
export function weatherFromQuery(search) {
  let params;
  if (search instanceof URLSearchParams) params = search;
  else if (typeof search === 'string') params = new URLSearchParams(search);
  else if (typeof window !== 'undefined') params = new URLSearchParams(window.location.search);
  else return null;

  const raw = params.get('weather');
  if (raw == null || raw === '') return null;
  const id = raw.toLowerCase();
  return WEATHER[id] ? id : null;
}

/**
 * Is `to` reachable from `from` in one step? The legality contract as a
 * function, so the suite and any future editor read the same table rather than
 * re-typing the rules.
 * @param {string} from
 * @param {string} to
 * @returns {boolean}
 */
export function canTransition(from, to) {
  const rows = WEATHER_TRANSITIONS[from];
  if (!rows) return false;
  return rows.some(([id]) => id === to);
}
